import React, { useEffect, useRef, useState, useMemo, useCallback  } from 'react';
import { getDistance } from 'geolib';
import { URL } from '../../utils/constants';
import './DrawPointOCKPanel.css';

export default function DrawPointOCKPanel({
  mapRef,
  ymapsApiRef,
  nearbySources = {},
  onPickingChange,
  onCreated,
  tokenProp,
  embedded = false,
  forceCollapsed,
  onCollapsedChange,
  activateExclusiveTool,
  releaseExclusiveTool,
  registerExclusiveToolStop,
  unregisterExclusiveToolStop,
  activeExclusiveTool,
  isToolBlocked,
  exclusiveTools,
  isMobile = false,
  existingOckPoints = [],
  duplicateRadiusMeters = 1,
}) {
  // ---- доступ только для группы OCK ----
  const [canUsePanel, setCanUsePanel] = useState(false);

  // ---- id текущего пользователя (может пригодиться для UI) ----
  const [userId, setUserId] = useState(null);
  const [collapsedLocal, setCollapsedLocal] = useState(() => {
    try { return localStorage.getItem('draw_panel_collapsed') === '1'; } catch { return false; }
  });
  const collapsed = (typeof forceCollapsed === "boolean") ? forceCollapsed : collapsedLocal;
  const setCollapsed = (vOrFn) => {
  const next = typeof vOrFn === "function" ? vOrFn(collapsed) : vOrFn;
    if (typeof forceCollapsed === "boolean") {
      onCollapsedChange?.(next);
    } else {
      setCollapsedLocal(next);
    }
  };
  useEffect(() => {
    if (typeof forceCollapsed === "boolean") return;
    try { localStorage.setItem('draw_panel_collapsed', collapsed ? '1' : '0'); } catch {}
  }, [collapsed, forceCollapsed]);

  const [isPicking, setIsPicking] = useState(false);
  const [point, setPoint] = useState(null); // [lat, lon]

  const pointRef = useRef(null);

  useEffect(() => {
    pointRef.current = point;
  }, [point]);

  // ---- поля PointOCK ----
  const [address, setAddress] = useState('');
  const [status, setStatus] = useState('created');
  const [capitalType, setCapitalType] = useState('not_specified');
  const [needsShurf, setNeedsShurf] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const [manager, setManager] = useState('');

  // NEW
  const [inventoryNumber, setInventoryNumber] = useState('');
  const [description, setDescription] = useState('');
  const [plannedFinishDate, setPlannedFinishDate] = useState(''); // YYYY-MM-DD

  const [objectRef, setObjectRef] = useState(''); // основной объект (id)
  const [mainStage, setMainStage] = useState(''); // главный этап (id)
  const [executors, setExecutors] = useState([]); // массив id (множественный)

  // справочники для селектов
  const [usersOptions, setUsersOptions] = useState([]);
  const [ockObjectsOptions, setOckObjectsOptions] = useState([]);
  const [ockStagesOptions, setOckStagesOptions] = useState([]);
  const [refsLoading, setRefsLoading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [successText, setSuccessText] = useState('');

  // Визуальные объекты выбора точки
  const placemarkRef = useRef(null);
  const haloRef = useRef(null);

  // ВАЖНО: храним именно callback обработчика клика карты
  const mapHandlersRef = useRef([]);
  const isPickingRef = useRef(false);

  const hasAccess = canUsePanel;

  useEffect(() => {
    isPickingRef.current = isPicking;
  }, [isPicking]);

  useEffect(() => {
    try {
      localStorage.setItem('ock_panel_collapsed', collapsed ? '1' : '0');
    } catch {}
  }, [collapsed]);

  // ---- определяем доступ по группе OCK ----
  useEffect(() => {
    const norm = (s) => String(s ?? '').toLowerCase().trim();

    const applyFromObj = (u = {}) => {
      const uid =
        u?.id ??
        u?.pk ??
        u?.user?.id ??
        (Array.isArray(u) && u.length ? (u[0]?.id ?? u[0]?.pk) : null);

      if (uid && !userId) setUserId(uid);

      let groups = u.groups || u.group_names || [];
      if (Array.isArray(groups) && groups.length && typeof groups[0] === 'object') {
        groups = groups.map((g) => g?.name);
      }

      const normalizedGroups = groups.map(norm);
      const hasOCK =
        normalizedGroups.includes('ock') ||
        normalizedGroups.includes('creator_ock') ||
        normalizedGroups.includes('point_ock') ||
        normalizedGroups.includes('strict point_ock');
      if (hasOCK) setCanUsePanel(true);
      return hasOCK;
    };

    // 1) localStorage
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      if (u && (u.id || u.pk || (u.user && u.user.id))) {
        applyFromObj(u);
      }
    } catch {}

    // 2) API
    const token = tokenProp || localStorage.getItem('auth_token');
    if (!token) return;

    const endpoints = [
      `${URL}/api/me/`,
      `${URL}/api/users/me/`,
      `${URL}/users/me/`,
      `${URL}/api/auth/users/me/`,
    ];

    (async () => {
      for (const ep of endpoints) {
        try {
          const r = await fetch(ep, { headers: { Authorization: `Token ${token}` } });
          if (!r.ok) continue;
          const data = await r.json();
          applyFromObj(data);
          break;
        } catch {}
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenProp]);

  // ---- загружаем справочники для селектов (пользователи / объекты / этапы) ----
  useEffect(() => {
    if (!hasAccess) return;

    const token = tokenProp || localStorage.getItem('auth_token');
    if (!token) return;

    let isCancelled = false;

    const fetchJson = async (url) => {
      const r = await fetch(url, {
        headers: { Authorization: `Token ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status} @ ${url}`);
      return r.json();
    };

    const normalizeArray = (data) => {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.results)) return data.results;
      return [];
    };

    (async () => {
      setRefsLoading(true);
      try {
        const [usersData, objectsData, stagesData] = await Promise.all([
          fetchJson(`${URL}/api/users/`).catch(() => []),
          fetchJson(`${URL}/api/ock-objects/`).catch(() => []),
          fetchJson(`${URL}/api/points-ock-stages/`).catch(() => []),
        ]);

        if (isCancelled) return;

        setUsersOptions(normalizeArray(usersData));
        setOckObjectsOptions(normalizeArray(objectsData));
        setOckStagesOptions(normalizeArray(stagesData));
      } catch (e) {
        if (!isCancelled) {
          console.warn('Не удалось загрузить справочники OCK:', e);
        }
      } finally {
        if (!isCancelled) setRefsLoading(false);
      }
    })();

    return () => {
      isCancelled = true;
    };
  }, [hasAccess, tokenProp]);

  useEffect(() => {
    return () => {
      onPickingChange?.(false);
    };
  }, [onPickingChange]);

  // ---- кандидаты для автоподстановки адреса ----
  const nearestCandidates = useMemo(() => {
    const out = [];

    const push = (item) => {
      if (!item) return;
      const lat = Number(item.lat);
      const lon = Number(item.long);

      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

      const label = item.address || item.name || item.representation_name || '';
      if (!label) return;

      out.push({
        lat,
        lon,
        label: String(label),
        raw: item,
        kind: item.__kind || 'object',
      });
    };

    (nearbySources.stations || []).forEach((s) => {
      push({
        ...s,
        __kind: 'station',
        address: s.address || s.name,
        lat: s.lat,
        long: s.long,
      });
    });

    (nearbySources.buildings || []).forEach((b) => {
      push({
        ...b,
        __kind: 'building',
        address: b.address || b.name,
        lat: b.lat,
        long: b.long,
      });
    });

    (nearbySources.hermodoors || []).forEach((h) => {
      push({
        ...h,
        __kind: 'hermodoor',
        address: h.address || h.name,
        lat: h.lat,
        long: h.long,
      });
    });

    return out;
  }, [nearbySources]);

  const findNearestMapObjectAddress = (coords) => {
    if (!Array.isArray(coords) || coords.length !== 2) return null;
    const [lat, lon] = coords;
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;

    let best = null;

    for (const c of nearestCandidates) {
      const d = getDistance(
        { latitude: lat, longitude: lon },
        { latitude: c.lat, longitude: c.lon }
      );

      if (!best || d < best.distance) {
        best = { ...c, distance: d };
      }
    }

    const MAX_DISTANCE_METERS = 250;
    if (best && best.distance <= MAX_DISTANCE_METERS) {
      return {
        address: best.label,
        source: 'map',
        distance: best.distance,
        kind: best.kind,
      };
    }

    return null;
  };

  const reverseGeocodeAddress = async (coords) => {
    try {
      const ymaps = ymapsApiRef?.current || window.ymaps;
      if (!ymaps?.geocode) return null;

      const res = await ymaps.geocode(coords, { results: 1 });
      const first = res?.geoObjects?.get?.(0);
      if (!first) return null;

      const meta = first.properties?.get?.('metaDataProperty');
      const geocoderMeta = meta?.GeocoderMetaData || {};
      const text =
        geocoderMeta?.text ||
        first.getAddressLine?.() ||
        first.properties?.get?.('text') ||
        first.properties?.get?.('name');

      if (!text) return null;

      return {
        address: String(text),
        source: 'geocode',
      };
    } catch (e) {
      console.warn('reverse geocode failed:', e);
      return null;
    }
  };

  const autofillAddressByCoords = async (coords) => {
    const hasManualAddress = String(address || '').trim().length > 0;

    const fromMap = findNearestMapObjectAddress(coords);
    if (fromMap?.address) {
      if (!hasManualAddress) setAddress(fromMap.address);
      return;
    }

    const fromGeo = await reverseGeocodeAddress(coords);
    if (fromGeo?.address) {
      if (!String(address || '').trim()) setAddress(fromGeo.address);
    }
  };

  const hasExistingOckPointNear = useCallback((lat, lon) => {
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;

    return (existingOckPoints || []).some((p) => {
      const pLat = Number(p?.lat);
      const pLon = Number(p?.long);

      if (!Number.isFinite(pLat) || !Number.isFinite(pLon)) return false;

      const distance = getDistance(
        { latitude: lat, longitude: lon },
        { latitude: pLat, longitude: pLon }
      );

      return distance <= duplicateRadiusMeters;
    });
  }, [existingOckPoints, duplicateRadiusMeters]);

  const addMapHandler = (map, event, fn) => {
    try { map.events.add(event, fn); } catch {}
    mapHandlersRef.current.push({ event, fn });
  };

  const removeAllMapHandlers = (map) => {
    if (!map) return;
    for (const h of mapHandlersRef.current) {
      try { map.events.remove(h.event, h.fn); } catch {}
    }
    mapHandlersRef.current = [];
  };

  const ockPickBlocked = isToolBlocked?.(exclusiveTools.OCK_PICK);

  const removePlacemark = useCallback(() => {
    const map = mapRef?.current;
    if (!map) return;

    if (placemarkRef.current) {
      try {
        map.geoObjects.remove(placemarkRef.current);
      } catch {}
      placemarkRef.current = null;
    }

    if (haloRef.current) {
      try {
        map.geoObjects.remove(haloRef.current);
      } catch {}
      haloRef.current = null;
    }
  }, [mapRef]);

  const stopPicking = useCallback(() => {
    const map = mapRef?.current;

    removeAllMapHandlers(map);

    setIsPicking(false);
    onPickingChange?.(false);
    releaseExclusiveTool?.(exclusiveTools.OCK_PICK);
  }, [mapRef, onPickingChange, releaseExclusiveTool]);

  const stopOckPickTool = useCallback(() => {
    stopPicking();
    removePlacemark();
  }, [stopPicking, releaseExclusiveTool]);

  useEffect(() => {
    if (!registerExclusiveToolStop || !unregisterExclusiveToolStop) return;

    registerExclusiveToolStop(exclusiveTools.OCK_PICK, stopOckPickTool);

    return () => {
      unregisterExclusiveToolStop(exclusiveTools.OCK_PICK, stopOckPickTool);
    };
  }, [registerExclusiveToolStop, unregisterExclusiveToolStop, stopOckPickTool]);

  useEffect(() => {
    if (!hasAccess) return;
    const map = mapRef?.current;
    if (!map) return;

    return () => {
      stopPicking();
      removePlacemark();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef, hasAccess, stopPicking, removePlacemark]);

  const placeOrMovePlacemark = (coords) => {
    const map = mapRef?.current;
    if (!map || !coords) return;
  
    const ymaps = ymapsApiRef?.current || window.ymaps;
    if (!ymaps?.Placemark) return;
  
    if (!placemarkRef.current) {
      placemarkRef.current = new ymaps.Placemark(
        coords,
        {
          balloonContent: 'Точка ОСК',
          iconCaption: 'ОСК',
          __isOCKPoint: true,
        },
        {
          preset: 'islands#redDotIcon',
          draggable: true,
          zIndex: 5000,
          zIndexHover: 5001,
        }
      );
    
      placemarkRef.current.events.add('dragend', async () => {
        const c = placemarkRef.current.geometry.getCoordinates();

        const [nextLat, nextLon] = c;

        if (hasExistingOckPointNear(nextLat, nextLon)) {
          const prevPoint = pointRef.current;

          if (prevPoint) {
            placemarkRef.current.geometry.setCoordinates(prevPoint);
            if (haloRef.current?.geometry?.setCoordinates) {
              haloRef.current.geometry.setCoordinates(prevPoint);
            }
          } else {
            removePlacemark();
            setPoint(null);
          }

          setErrorText('Нельзя поставить точку в то же место');
          return;
        }
        setPoint(c);
      
        if (haloRef.current?.geometry?.setCoordinates) {
          haloRef.current.geometry.setCoordinates(c);
        }
      
        await autofillAddressByCoords(c);
      });
    
      map.geoObjects.add(placemarkRef.current);
    } else {
      placemarkRef.current.geometry.setCoordinates(coords);
    
      // ВАЖНО: сбрасываем стиль "под новую точку"
      try {
        placemarkRef.current.options.set('preset', 'islands#redDotIcon');
        placemarkRef.current.options.set('draggable', true);
      } catch {}
    }
  
    try {
      if (ymaps?.Circle) {
        if (!haloRef.current) {
          haloRef.current = new ymaps.Circle(
            [coords, 18],
            {},
            {
              fillColor: 'rgba(111,66,193,0.18)',
              strokeColor: '#6f42c1',
              strokeWidth: 2,
              strokeOpacity: 0.9,
              zIndex: 4999,
              interactivityModel: 'default#transparent',
            }
          );
          map.geoObjects.add(haloRef.current);
        } else {
          haloRef.current.geometry.setCoordinates(coords);
        
          // Сброс halo, если ранее стал зелёным после сохранения
          try {
            haloRef.current.options.set('fillColor', 'rgba(111,66,193,0.18)');
            haloRef.current.options.set('strokeColor', '#6f42c1');
          } catch {}
        }
      }
    } catch (e) {
      console.warn('Halo circle failed:', e);
    }
  };

  const startPicking = () => {
    if (!hasAccess) return;
    if (ockPickBlocked) return;

    activateExclusiveTool?.(exclusiveTools.OCK_PICK);

    setErrorText('');
    setSuccessText('');

    const map = mapRef?.current;
    if (!map) return;

    // не вешаем обработчик повторно
    if (isPickingRef.current || mapHandlersRef.current.length > 0) return;

    setIsPicking(true);
    onPickingChange?.(true);

    const onMapClick = async (e) => {
      // защита от "залипшего" обработчика
      if (!isPickingRef.current) return;

      const coords = e.get('coords'); // [lat, lon]

      // защита от тапа по панели
      if (!Array.isArray(coords) || coords.length !== 2) return;

      const [lat, lon] = coords;

      if (hasExistingOckPointNear(lat, lon)) {
        setErrorText('Нельзя поставить точку в то же место');
        setPoint(null);
        removePlacemark();
        stopPicking();
        return;
      }

      setPoint(coords);
      placeOrMovePlacemark(coords);

      await autofillAddressByCoords(coords);

      // one-shot: после первого клика сразу снимаем обработчик
      stopPicking();
    };

    //  на тач-устройствах Я.Карты чаще отдают tap
    addMapHandler(map, "tap", onMapClick);

    //  на десктопе и части устройств работает click
    addMapHandler(map, "click", onMapClick);

    //  fallback: иногда помогает, если click/tap не приходят
    addMapHandler(map, "touchstart", onMapClick);
  };

  const toNumberOrNull = (val) => {
    if (val === '' || val === null || val === undefined) return null;
    const n = Number(val);
    return Number.isFinite(n) ? n : null;
  };

  // Формат: "Фамилия И.О." (если отчества нет -> "Фамилия И.")
  const formatUserShortName = (u) => {
    const lastName = String(u?.last_name || '').trim();
    const firstName = String(u?.first_name || '').trim();
    const middleName = String(u?.middle_name || u?.patronymic || '').trim();

    const firstInitial = firstName ? `${firstName[0]}.` : '';
    const middleInitial = middleName ? `${middleName[0]}.` : '';

    if (lastName && (firstInitial || middleInitial)) {
      return `${lastName} ${firstInitial}${middleInitial}`.trim();
    }

    if (lastName) return lastName;
    if (firstName) return firstName;

    return String(u?.username || `ID ${u?.id ?? ''}`).trim();
  };

  const readMultiSelectValues = (e) =>
    Array.from(e.target.selectedOptions || [])
      .map((opt) => Number(opt.value))
      .filter((n) => Number.isFinite(n));

  const buildPayload = () => {
    if (!point) throw new Error('Не выбрана точка на карте');

    const payload = {
      address: address.trim(),
      lat: point[0],
      long: point[1],
      status,
      capital_type: capitalType,
      needs_shurf: !!needsShurf,
      is_visible: !!isVisible,
    };

    if (inventoryNumber.trim()) payload.inventory_number = inventoryNumber.trim();
    if (description.trim()) payload.description = description.trim();
    if (plannedFinishDate) payload.planned_finish_date = plannedFinishDate;

    const managerId = toNumberOrNull(manager);
    if (managerId) payload.manager = managerId;

    const objectRefId = toNumberOrNull(objectRef);
    if (objectRefId) {
      payload.object_ref = objectRefId;
      // Чтобы пройти серверную валидацию (object_ref должен входить в objects)
      payload.objects_ock = [objectRefId];
    }

    const mainStageId = toNumberOrNull(mainStage);
    if (mainStageId) {
      payload.main_stage = mainStageId;
      // Чтобы пройти серверную валидацию (main_stage должен входить в stages)
      payload.stages = [mainStageId];
    }

    if (executors.length) payload.executors = executors;

    // creator НЕ отправляем — бек подставляет автоматически
    return payload;
  };

  const handleSave = async () => {
    if (!hasAccess) return;

    setErrorText('');
    setSuccessText('');

    let payload;
    try {
      payload = buildPayload();
    } catch (e) {
      setErrorText(e.message || 'Ошибка подготовки данных');
      return;
    }

    const token = tokenProp || localStorage.getItem('auth_token');
    if (!token) {
      setErrorText('Нет токена авторизации');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${URL}/api/points-ock/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          typeof data === 'object' ? JSON.stringify(data, null, 2) : `HTTP ${res.status}`
        );
      }

      setSuccessText(`Точка ОСК создана${data?.id ? ` (ID: ${data.id})` : ''}`);
      onCreated?.(data);

      // reset (координату оставляем на карте как подтверждение)
      setAddress('');
      setStatus('created');
      setCapitalType('not_specified');
      setNeedsShurf(false);
      setIsVisible(true);

      setManager('');
      setExecutors([]);

      setInventoryNumber('');
      setDescription('');
      setPlannedFinishDate('');

      setObjectRef('');
      setMainStage('');

      stopPicking();

      removePlacemark();
    } catch (e) {
      setErrorText(e.message || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setPoint(null);

    setAddress('');
    setStatus('created');
    setCapitalType('not_specified');
    setNeedsShurf(false);
    setIsVisible(true);

    setManager('');
    setExecutors([]);

    setInventoryNumber('');
    setDescription('');
    setPlannedFinishDate('');

    setObjectRef('');
    setMainStage('');

    setErrorText('');
    setSuccessText('');

    stopPicking();
    removePlacemark();
  };

  if (!hasAccess) return null;

  return (
    <>
      {!embedded&& (
        <div className="ock-toggle-wrap">
            <button
            className="ock-toggle-btn"
            aria-expanded={!collapsed}
            title={collapsed ? 'Развернуть панель ОСК' : 'Свернуть панель ОСК'}
            onClick={() => setCollapsed((v) => !v)}
            >
            {collapsed ? 'ОСК ▸' : 'ОСК ▾'}
            </button>
        </div>
      )}

      {!collapsed && (
        <div className={`ock-panel ${embedded ? "ock-panel--embedded" : ""}`}>
          <div className="ock-panel__title">Создание точки ОСК</div>

          <button
            onClick={isPicking ? stopPicking : startPicking}
            disabled={ockPickBlocked}
            className={`ock-btn ${isPicking ? 'ock-btn--pick-cancel' : 'ock-btn--pick'}`}
          >
            {isPicking ? 'Отменить выбор точки' : 'Выбрать точку на карте'}
          </button>

          <div className="ock-coords-box">
            {point ? (
              <div className="ock-coords-text">
                <div><b>Широта:</b> {point[0].toFixed(6)}</div>
                <div><b>Долгота:</b> {point[1].toFixed(6)}</div>
              </div>
            ) : (
              <div className="ock-coords-empty">Точка не выбрана</div>
            )}
          </div>

          {/* Адрес — не трогаем */}
          {(!isMobile || !isPicking) && (
            <>
              <input
                className="ock-input"
                placeholder="Адрес"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />

              <input
                className="ock-input"
                placeholder="Инвентарный номер (опц.)"
                value={inventoryNumber}
                onChange={(e) => setInventoryNumber(e.target.value)}
              />

              <textarea
                className="ock-input"
                placeholder="Описание (опц.)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                style={{ resize: 'vertical' }}
              />

              <select
                className="ock-select"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="created">Создана</option>
                <option value="in_work">В работе</option>
                <option value="ready">Готово</option>
                <option value="done">Выполнена</option>
              </select>

              <select
                className="ock-select"
                value={capitalType}
                onChange={(e) => setCapitalType(e.target.value)}
              >
                <option value="not_specified">Не указана</option>
                <option value="capital">Капитальная</option>
                <option value="non_capital">Не капитальная</option>
              </select>

              {/* Руководитель */}
              <select
                className="ock-select"
                value={manager}
                onChange={(e) => setManager(e.target.value)}
              >
                <option value="">Руководитель (опц.)</option>
                {usersOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {formatUserShortName(u)}
                  </option>
                ))}
              </select>

              {/* Исполнители */}
              <label className="ock-label">
                Исполнители (можно несколько, опц.)
              </label>
              <select
                className="ock-select"
                multiple
                value={executors.map(String)}
                onChange={(e) => setExecutors(readMultiSelectValues(e))}
                size={Math.min(6, Math.max(3, usersOptions.length || 3))}
              >
                {usersOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {formatUserShortName(u)}
                  </option>
                ))}
              </select>

              {/* Основной объект */}
              <select
                className="ock-select"
                value={objectRef}
                onChange={(e) => setObjectRef(e.target.value)}
              >
                <option value="">Основной объект (опц.)</option>
                {ockObjectsOptions.map((obj) => (
                  <option key={obj.id} value={obj.id}>
                    {obj.name}
                  </option>
                ))}
              </select>

              {/* Главный этап */}
              <select
                className="ock-select"
                value={mainStage}
                onChange={(e) => setMainStage(e.target.value)}
              >
                <option value="">Главный этап (опц.)</option>
                {ockStagesOptions.map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.stage_type_display ? `${st.stage_type_display} — ${st.name}` : st.name}
                  </option>
                ))}
              </select>

              {/* Плановая дата завершения */}
              <label className="ock-label">
                Плановая дата завершения (опц.)
              </label>
              <input
                className="ock-input"
                type="date"
                value={plannedFinishDate}
                onChange={(e) => setPlannedFinishDate(e.target.value)}
              />

              <label className="ock-checkbox-label ock-checkbox-label--mb6">
                <input
                  type="checkbox"
                  checked={needsShurf}
                  onChange={(e) => setNeedsShurf(e.target.checked)}
                />
                <span>Требуется шурф</span>
              </label>

              <label className="ock-checkbox-label ock-checkbox-label--mb10">
                <input
                  type="checkbox"
                  checked={isVisible}
                  onChange={(e) => setIsVisible(e.target.checked)}
                />
                <span>Видимая точка</span>
              </label>
            </>
          )}

          {refsLoading && <div className="ock-hint">Загрузка списков пользователей / объектов / этапов…</div>}
          {errorText && <div className="ock-error">{errorText}</div>}
          {successText && <div className="ock-success">{successText}</div>}

          <button
            onClick={handleSave}
            disabled={saving || !point}
            className="ock-btn ock-btn--save"
          >
            {saving ? 'Сохранение...' : 'Сохранить точку ОСК'}
          </button>

          <button
            onClick={handleClear}
            className="ock-btn ock-btn--clear"
          >
            Очистить
          </button>
        </div>
      )}
    </>
  );
}